import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { canManagePaymentProviderSelector } from '@/domain/env/envSelector';
import { hasNonDisconnectedPaymentProviderSelector } from '@/domain/paySettings/selectorForPermissions';

import { connectProviderAction, disconnectProviderAction } from '@/domain/paySettings/actions';

import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ContextMenuButton from '@/components/mui/ContextMenuButton';

import { PaymentProviderStatuses } from '@/domain/paySettings/helpers';
import type { PaymentProviderName } from '@/domain/paySettings/helpers';
import { contextMenuActionsList } from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/Provider/contextMenuActionsList';

import {
  createStatuses,
  Button,
  Container,
} from '@/pages/dokkaPay/pages/configurations/company/pages/paymentProviders/components/Providers/components/Provider/styled';

interface ProviderProps {
  title: string;
  status: string;
  icon: string;
  name: PaymentProviderName;
  id: string | null;
  setSelectedProviderId: (id: string | null) => void;
}

const Provider: React.FC<ProviderProps> = ({ title, status, icon, name, id, setSelectedProviderId }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const canManagePaymentProviderById = useMemo(() => canManagePaymentProviderSelector(id), [id]);
  const canManagePaymentProvider = useSelector(canManagePaymentProviderById);
  const hasNonDisconnectedPaymentProvider = useSelector(hasNonDisconnectedPaymentProviderSelector);

  const onConnectClick = useCallback(() => {
    dispatch(connectProviderAction({ providerName: name }));
  }, [dispatch, name]);

  const onDisconnectClick = useCallback(() => {
    dispatch(disconnectProviderAction({ id, providerName: name }));
  }, [dispatch, id, name]);

  const onEditClick = useCallback(() => {
    setSelectedProviderId(id);
  }, [setSelectedProviderId, id]);

  const contextMenuActions = useMemo(
    () =>
      ({
        'configurations.company.paymentProviders.provider.contextMenu.viewTransactions': {
          onClick: () => {},
        },
        'configurations.company.paymentProviders.provider.contextMenu.disconnect': {
          onClick: onDisconnectClick,
          isHidden: status === PaymentProviderStatuses.disconnected,
        },
      }) as Record<string, { onClick: () => void; isHidden?: boolean }>,
    [status, onDisconnectClick],
  );

  const optionsList = useMemo(
    () =>
      contextMenuActionsList
        .map(({ id, defaultMessage, ...rest }) => ({
          id,
          title: { id, defaultMessage },
          ...rest,
          ...contextMenuActions[id],
        }))
        .filter(({ isHidden }) => !isHidden),
    [contextMenuActions],
  );

  const renderButton = useCallback(
    ({ status }: { status: string }) => {
      if (status === PaymentProviderStatuses.disconnected) {
        return (
          <Button onClick={onConnectClick} disabled={hasNonDisconnectedPaymentProvider}>
            {formatMessage({ id: 'settings.connections.table.connect', defaultMessage: 'Connect' })}
          </Button>
        );
      }

      if (!canManagePaymentProvider || status !== PaymentProviderStatuses.connected) return null;

      return (
        <Stack direction="row" spacing={1}>
          <ContextMenuButton options={optionsList} contextMenuName="PaymentProvider" />
          <Button variant="outlined" onClick={onEditClick}>
            {formatMessage({ id: 'button.edit', defaultMessage: 'Edit' })}
          </Button>
        </Stack>
      );
    },
    [
      formatMessage,
      onConnectClick,
      onEditClick,
      optionsList,
      canManagePaymentProvider,
      hasNonDisconnectedPaymentProvider,
    ],
  );

  return (
    <Container>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flex="1" flexWrap="nowrap">
        <Stack display="flex" direction="row" spacing={1} alignItems="center">
          <Stack
            justifyContent="center"
            alignItems="center"
            sx={(theme) => ({
              padding: theme.spacing(0.5, 1),
              border: 1,
              borderColor: theme.palette.grey[200],
              backgroundColor: theme.palette.common.white,
              borderRadius: 2,
              minHeight: 34,
            })}
          >
            <img width={40} height="auto" src={icon} alt={title} />
          </Stack>
          <Typography>{[title]}</Typography>
          {createStatuses[status]}
        </Stack>
        <Box>{renderButton({ status })}</Box>
      </Stack>
    </Container>
  );
};

export default Provider;
